/* eslint-disable quote-props */
import {HeroIngredient, InstantCheckupAiResponseResultItem} from '@cureskin/api-client/src/server-api';
export { PRIVATE_MAIN_CONCERN_DISPLAY_NAME } from '../user-regimen/regimen-products-detail/doctor-notes-constants';

export interface ConcernsWithCount {
  infAcne: number;
  comedone: number;
  openPores: number;
  ds: number;
  dc: number;
  melasma: number;
  acneScars: number;
  sensitiveSkin: number;
}

export interface SkinParameters {
  patientScore: number;
  expectedRange: string;
}

export interface RootCauses {
  [key: string]: string;
}

export interface SkinData {
  sebumBalanceParameters: SkinParameters;
  inflammationParameters: SkinParameters;
  sensitivityParameters: SkinParameters;
  skinDamageParameters: SkinParameters;
  skinRenewalParameters: SkinParameters;
  skinRadianceParameters: SkinParameters;
}

export interface HairData {
  hairDensityParameters: SkinParameters;
  hairStrengthParameters: SkinParameters;
  scalpOilBalanceParameters: SkinParameters;
  hairSmoothnessParameters: SkinParameters;
  prematureGreyingParameters: SkinParameters;
  greyingParameters: SkinParameters;
}

export interface DetectionResult extends InstantCheckupAiResponseResultItem {
  Severity?: string;
}

export interface SkinReportInfo {
  skinParameters?: SkinData;
  overallSummary?: { overallSummary?: string };
}

export type HairFallbackEntry = Partial<Record<keyof HairData, SkinParameters | null>>;

export const HAIR_REPORT_FALLBACK_PARAMS: Readonly<Record<string, Record<string, HairFallbackEntry>>> = {
  DANDRUFF: {
    Moderate: {
      hairDensityParameters: null,
      hairStrengthParameters: { patientScore: 6, expectedRange: '8-10' },
      scalpOilBalanceParameters: { patientScore: 8, expectedRange: '4-6' },
      hairSmoothnessParameters: { patientScore: 5, expectedRange: '8-10' },
    },
  },
  DANDRUFF_AND_HAIR_LOSS: {
    Moderate: {
      hairDensityParameters: { patientScore: 4, expectedRange: '8-10' },
      hairStrengthParameters: { patientScore: 5, expectedRange: '8-10' },
      scalpOilBalanceParameters: { patientScore: 8, expectedRange: '4-6' },
      hairSmoothnessParameters: null,
    },
  },
  FRIZZY_HAIR: {
    Moderate: {
      hairDensityParameters: null,
      hairStrengthParameters: { patientScore: 4, expectedRange: '8-10' },
      scalpOilBalanceParameters: { patientScore: 4, expectedRange: '4-6' },
      hairSmoothnessParameters: { patientScore: 3, expectedRange: '8-10' },
    },
  },
  FRIZZY_HAIR_AND_DANDRUFF: {
    Moderate: {
      hairDensityParameters: null,
      hairStrengthParameters: { patientScore: 3, expectedRange: '8-10' },
      scalpOilBalanceParameters: { patientScore: 8, expectedRange: '4-6' },
      hairSmoothnessParameters: { patientScore: 2, expectedRange: '8-10' },
    },
  },
  FRIZZY_HAIR_AND_HAIR_LOSS: {
    Moderate: {
      hairDensityParameters: { patientScore: 5, expectedRange: '8-10' },
      hairStrengthParameters: { patientScore: 4, expectedRange: '8-10' },
      scalpOilBalanceParameters: null,
      hairSmoothnessParameters: { patientScore: 3, expectedRange: '8-10' },
    },
  },
  HAIR_LOSS: {
    Mild: {
      hairDensityParameters: { patientScore: 6, expectedRange: '8-10' },
      hairStrengthParameters: { patientScore: 7, expectedRange: '8-10' },
      scalpOilBalanceParameters: { patientScore: 5, expectedRange: '4-6' },
      hairSmoothnessParameters: null,
    },
    Moderate: {
      hairDensityParameters: { patientScore: 4, expectedRange: '8-10' },
      hairStrengthParameters: { patientScore: 5, expectedRange: '8-10' },
      scalpOilBalanceParameters: { patientScore: 6, expectedRange: '4-6' },
      hairSmoothnessParameters: null,
    },
    Severe: {
      hairDensityParameters: { patientScore: 2, expectedRange: '8-10' },
      hairStrengthParameters: { patientScore: 4, expectedRange: '8-10' },
      scalpOilBalanceParameters: { patientScore: 5, expectedRange: '4-6' },
      hairSmoothnessParameters: null,
    },
  },
  REGULAR_HAIR_CARE: {
    Moderate: {
      hairDensityParameters: { patientScore: 7, expectedRange: '8-10' },
      hairStrengthParameters: { patientScore: 9, expectedRange: '8-10' },
      scalpOilBalanceParameters: { patientScore: 6, expectedRange: '4-6' },
      hairSmoothnessParameters: null,
    },
  },
  GREY_HAIR: {
    Moderate: {
      hairDensityParameters: { patientScore: 6, expectedRange: '8-10' },
      hairStrengthParameters: { patientScore: 8, expectedRange: '8-10' },
      scalpOilBalanceParameters: { patientScore: 5, expectedRange: '4-6' },
      hairSmoothnessParameters: null,
    },
  },
};

export interface ConcernCountResult {
  payload: ConcernsWithCount;
  mainConcernKey: string;
  mainConcern: string;
}

export interface HeroIngredientsResult {
  ingredients: HeroIngredient[];
  show: boolean;
}

export const HAIR_CONCERNS = ['hairline', 'temple-baldness', 'scalp-segment'];

export const CONCERN_NAMES: Record<string, string> = {
  infAcne: 'Acne',
  comedone: 'Comedones',
  openPores: 'Open pores',
  ds: 'Dark spots',
  dc: 'Dark circles',
  melasma: 'Melasma',
  acneScars: 'Acne Scars',
  sensitiveSkin: 'Sensitive Skin',
};

export const PROBLEM_TO_CONCERN: Record<string, { key: string, useBoundingBoxCount: boolean }> = {
  'Inflammatory Acne': { key: 'infAcne', useBoundingBoxCount: true },
  'Comedones': { key: 'comedone', useBoundingBoxCount: true },
  'Acne Scars': { key: 'acneScars', useBoundingBoxCount: true },
  'Dark Spots': { key: 'ds', useBoundingBoxCount: true },
  'Open Pores': { key: 'openPores', useBoundingBoxCount: false },
  'Melasma': { key: 'melasma', useBoundingBoxCount: false },
  'Dark Circles': { key: 'dc', useBoundingBoxCount: false },
};

export const FIRST_JOURNEY_CONCERNS = ['Pigmentation', 'Acne Scars', 'Dark spots'];


export const skinParameterCards: { paramKey: keyof SkinData, severityKey: string, title: string, description: string }[] = [
  {
    paramKey: 'sebumBalanceParameters',
    severityKey: 'sebumBalance',
    title: 'Sebum Balance <br> /Oiliness',
    description: 'Sebum is an oily substance produced by the sebaceous glands in the dermis of the skin, and it plays a crucial role in maintaining the health and integrity of the skin.',
  },
  {
    paramKey: 'inflammationParameters',
    severityKey: 'inflammation',
    title: 'Inflammation/<br>Redness',
    description: 'Inflammation on the skin signify the presence of an injury, physical trauma, infection, or irritation in that area, which is commonly associated with acne',
  },
  {
    paramKey: 'sensitivityParameters',
    severityKey: 'senstivity',
    title: 'Skin Sensitivity <br>Range',
    description: 'Skin condition characterized by heightened reactivity and a low tolerance threshold to various external factors such as burning, itching, dryness etc.',
  },
  {
    paramKey: 'skinRenewalParameters',
    severityKey: 'skinRenewal',
    title: 'Skin Renewal <br> Rate',
    description: 'The skin\'s natural process of shedding dead cells and replacing them with new ones maintains its health, appearance, and functionality through this renewal cycle.',
  },
  {
    paramKey: 'skinRadianceParameters',
    severityKey: 'skinRadiance',
    title: 'Skin Radiance',
    description: 'Skin radiance is the natural glow, vitality, and luminosity of skin described by healthy, well-hydrated, and youthful visual appearance.',
  },
  {
    paramKey: 'skinDamageParameters',
    severityKey: 'skinDamage',
    title: 'Skin Damage Rate',
    description: 'The frequency at which damage occurs to the skin tissues can result from various factors, including inflammatory acne, sun exposure, and skin sensitivity.',
  },
];

export const hairParameterCards: { paramKey: keyof HairData, severityKey: string, title: string, description: string }[] = [
  {
    paramKey: 'hairDensityParameters',
    severityKey: 'hairDensity',
    title: 'Hair Density',
    description: 'Hair density refers to the number of hair strands per square centimeter on the scalp, indicating how thick or thin the overall hair coverage appears.',
  },
  {
    paramKey: 'hairStrengthParameters',
    severityKey: 'hairStrength',
    title: 'Hair Strength',
    description: 'Hair strength measures the resilience and elasticity of individual hair strands, reflecting their ability to withstand breakage and damage.',
  },
  {
    paramKey: 'scalpOilBalanceParameters',
    severityKey: 'scalpOilBalance',
    title: 'Scalp Oil Balance',
    description: 'Scalp oil balance indicates the level of sebum production on the scalp, which affects hair health, texture, and overall scalp condition.',
  },
  {
    paramKey: 'hairSmoothnessParameters',
    severityKey: 'hairSmoothness',
    title: 'Hair Smoothness',
    description: 'Hair smoothness reflects the texture and cuticle health of hair strands, indicating how well-nourished and hydrated the hair is.',
  },
  {
    paramKey: 'prematureGreyingParameters',
    severityKey: 'prematureGreying',
    title: 'Premature Greying',
    description: 'Premature greying refers to the early loss of hair pigmentation, often influenced by genetics, stress, and nutritional deficiencies.',
  },
  {
    paramKey: 'greyingParameters',
    severityKey: 'greying',
    title: 'Greying',
    description: 'Greying indicates the natural progression of hair pigment loss over time, reflecting the overall melanin production in hair follicles.',
  },
];

export const DefaultDrVideo = ['https://cdn.cureskin.com/app/video/drCharu_Eng_1_skin.mp4'];
export const DefaultDrImages = [
  'https://cdn.cureskin.com/app/img/timer-page-inline-en-1.png',
  'https://cdn.cureskin.com/app/img/timer-page-inline-en-2.png',
];
